import { useMemo } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from "recharts";

const DEFAULT_COLOR = "#1976d2";

const formatTimeLabel = (hourValue) => {
  if (!Number.isFinite(hourValue)) {
    return "";
  }
  const totalMinutes = Math.round(hourValue * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const formatTimeWithMeridiem = (hourValue) => {
  if (!Number.isFinite(hourValue)) {
    return "";
  }
  const totalMinutes = Math.round(hourValue * 60);
  const hours24 = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  const period = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${String(hours12).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${period}`;
};

const parseRoutineTime = (actionTime) => {
  if (actionTime == null) {
    return null;
  }
  if (typeof actionTime === "number" && Number.isFinite(actionTime)) {
    const date = new Date(actionTime * 1000);
    return (
      date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600
    );
  }
  if (typeof actionTime === "string") {
    const trimmed = actionTime.trim();
    if (!trimmed) {
      return null;
    }
    if (/^\d+(\.\d+)?$/.test(trimmed)) {
      const numeric = Number(trimmed);
      if (Number.isFinite(numeric)) {
        const date = new Date(numeric * 1000);
        return (
          date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600
        );
      }
    }
    if (trimmed.includes(":")) {
      const parts = trimmed.split(":").map((part) => Number(part));
      if (parts.length >= 2 && parts.every((value) => Number.isFinite(value))) {
        const [hours, minutes, seconds = 0] = parts;
        return hours + minutes / 60 + seconds / 3600;
      }
    }
  }
  return null;
};

const normalizeAction = (action) => {
  if (typeof action === "string") {
    const normalized = action.trim().toUpperCase();
    if (normalized === "ON") {
      return 1;
    }
    if (normalized === "OFF") {
      return 0;
    }
  }
  return Number(action);
};

const buildChannelKey = (channel, index) =>
  channel?.channelName ? channel.channelName : `Channel ${index + 1}`;

const buildActiveChannelMap = (spectrum) => {
  const channelData = spectrum?.channelData;
  if (!Array.isArray(channelData)) {
    return new Map();
  }
  const map = new Map();
  channelData.forEach((channel, index) => {
    if (!channel?.isActive) {
      return;
    }
    const key = buildChannelKey(channel, index);
    map.set(key, Number(channel.channelValue) || 0);
  });
  return map;
};

const buildScheduleData = (routines, spectrumById) => {
  const normalizedRoutines = (routines || [])
    .filter((routine) => routine && routine.status !== 0)
    .map((routine) => {
      let time = parseRoutineTime(routine.actionTime);
      if (Number.isFinite(time) && time >= 24) {
        time = time % 24;
      }
      return {
        ...routine,
        normalizedAction: normalizeAction(routine.action),
        time,
      };
    })
    .filter((routine) => Number.isFinite(routine.time))
    .sort((a, b) => {
      if (a.time !== b.time) {
        return a.time - b.time;
      }
      return (a.routineId ?? 0) - (b.routineId ?? 0);
    });

  const hasOn = normalizedRoutines.some((r) => r.normalizedAction === 1);
  if (!hasOn) {
    return { data: [], channels: [], markers: [] };
  }

  const activeChannelKeys = new Set();
  normalizedRoutines
    .filter((routine) => routine.normalizedAction === 1)
    .forEach((routine) => {
      const spectrum =
        spectrumById?.[routine.spectrumId] ||
        spectrumById?.[routine.spectrumControlId];
      const channelData = spectrum?.channelData;
      if (!Array.isArray(channelData)) {
        return;
      }
      channelData.forEach((channel, index) => {
        if (!channel?.isActive) {
          return;
        }
        activeChannelKeys.add(buildChannelKey(channel, index));
      });
    });

  if (!activeChannelKeys.size) {
    return { data: [], channels: [], markers: [] };
  }

  const channels = Array.from(activeChannelKeys).map((name) => ({ name }));

  const spectrumMapForRoutine = (routine) =>
    spectrumById?.[routine.spectrumId] ||
    spectrumById?.[routine.spectrumControlId];

  const stateAfterEvent = (routine) => {
    const out = {};
    channels.forEach((ch) => {
      out[ch.name] = 0;
    });
    if (routine.normalizedAction !== 1) {
      return out;
    }
    const channelMap = buildActiveChannelMap(spectrumMapForRoutine(routine));
    channels.forEach((ch) => {
      out[ch.name] = channelMap.get(ch.name) || 0;
    });
    return out;
  };

  const n = normalizedRoutines.length;
  /** Midnight–first event follows last event (wrap past 24:00). */
  const initialAtMidnight = stateAfterEvent(normalizedRoutines[n - 1]);

  const data = [];
  const markers = normalizedRoutines.map((r) =>
    Math.min(24, Math.max(0, r.time))
  );

  const firstT = normalizedRoutines[0].time;
  if (firstT > 1e-6) {
    data.push({
      time: 0,
      ...initialAtMidnight,
      routineName: "",
    });
  }

  for (let i = 0; i < n; i += 1) {
    const routine = normalizedRoutines[i];
    const t = Math.min(24, Math.max(0, routine.time));
    const next = stateAfterEvent(routine);
    data.push({
      time: t,
      ...next,
      routineName: routine.routineName || "",
    });
  }

  data.push({
    time: 24,
    ...stateAfterEvent(normalizedRoutines[n - 1]),
    routineName: "",
  });

  return { data, channels, markers };
};

export default function RoutineScheduleChart({
  routines = [],
  spectrumById = {},
  loading = false,
  errorMsg = "",
  colorMap = {},
  height = 260,
}) {
  const { data, channels, markers } = useMemo(
    () => buildScheduleData(routines, spectrumById),
    [routines, spectrumById]
  );

  const hasData = data.length > 1 && channels.length > 0;
  const xTicks = useMemo(() => {
    const ticks = [];
    for (let hour = 0; hour <= 24; hour += 2) {
      ticks.push(hour);
    }
    return ticks;
  }, []);

  const renderTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) {
      return null;
    }
    const routineName =
      payload[0]?.payload?.routineName || "";
    return (
      <Box
        sx={{
          bgcolor: "#ffffff",
          border: "1px solid #E2E8F0",
          borderRadius: 0.5,
          px: 0.5,
          py: 0.25,
          fontSize: 8,
          color: "#000000",
          fontWeight: 600,
          lineHeight: 0.8,
          minWidth: 120,
        }}
      >
        {routineName && (
          <Box sx={{ mb: 0.25, fontWeight: 700, textAlign: "right" }}>
            {routineName}
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 0.25,
          }}
        >
          <span>TIME</span>
          <span>{formatTimeWithMeridiem(label)}</span>
        </Box>
        {payload.map((entry) => (
          <Box
            key={entry.dataKey}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 0.25,
            }}
          >
            <span>{entry.name}</span>
            <span>{Math.round(entry.value)}%</span>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mb: 0.5, fontWeight: 400, fontSize: "1.25rem" }}
        >
          Routine
        </Typography>
      </Stack>
      <Paper elevation={1} sx={{ p: 1, pl: 0.25 }}>
        {loading && (
          <Typography variant="body2">Loading routines...</Typography>
        )}
        {!loading && errorMsg && (
          <Typography variant="body2">{errorMsg}</Typography>
        )}
        {!loading && !errorMsg && !hasData && (
          <Typography variant="body2">
            Create routines to see the 24-hour schedule.
          </Typography>
        )}
        {!loading && !errorMsg && hasData && (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={data}>
              <XAxis
                dataKey="time"
                type="number"
                domain={[0, 24]}
                ticks={xTicks}
                tickFormatter={formatTimeLabel}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
                tickFormatter={(value) => Math.round(value)}
                width={40}
                tick={{ fontSize: 11 }}
              />
              <Tooltip content={renderTooltip} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              {markers.map((time, index) => (
                <ReferenceLine
                  key={`${time}-${index}`}
                  x={time}
                  stroke="#94A3B8"
                  strokeDasharray="3 3"
                />
              ))}
              {channels.map((channel) => (
                <Line
                  key={channel.name}
                  type="stepAfter"
                  dataKey={channel.name}
                  stroke={colorMap[channel.name] || DEFAULT_COLOR}
                  strokeWidth={2.4}
                  strokeLinecap="butt"
                  dot={{
                    r: 2,
                    fill: colorMap[channel.name] || DEFAULT_COLOR,
                    stroke: colorMap[channel.name] || DEFAULT_COLOR,
                  }}
                  activeDot={{
                    r: 2,
                    fill: colorMap[channel.name] || DEFAULT_COLOR,
                    stroke: colorMap[channel.name] || DEFAULT_COLOR,
                  }}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Paper>
    </Box>
  );
}
